import { array, object, string } from 'yup';
import {
  AppointmentFulfillmentType,
  AssigneeActivityAPI,
  CheckInSmallParcelReceiptRequest,
  CreateReceiptItemAliasAPI,
  FileType,
  ReceiptAPI,
  ReceiptCarrierArrangedByType,
  ReceiptDocument,
  ReceiptFulfillmentType,
  ReceiptItem,
  ReceiptPackage,
  ReceiptPackageType,
  ReceiptPhysicalItem,
  ReceiptReturnInspectionType,
  ReceiptShipment,
  ReceiptStatusType,
  ReceiptType,
  UnitOfMeasureCategoryType,
  UserPerTaskType,
} from '../../types';
import {
  makeuuid,
  convertNullFieldsToEmptyString,
  createNestedSchema,
} from '../../lib';

export interface ReceiptRouteParams {
  RECEIPT_ID?: string;
}

export interface CreateReceiptPhysicalItem extends ReceiptPhysicalItem {
  moveableUnitPhysicalItemId?: any;
}

interface DefaultItemAutocompleteOption {
  id: number | string;
  name: string;
}

export type PackagingMode = 'simple' | 'packages';

export interface SimpleReceiptItem {
  itemUnitOfMeasureID: number | string;
  quantity: number | string;
  itemType: 'sku' | 'alias';
  aliasID?: number | string;
  unitOfMeasureType?: string;
  isPrimaryUnitPallet?: boolean;
  sku?: string;
  customerItemID?: number | string;
  defaultOption?: { id: number | string; name: string };
  createPhysicalItems: Record<string, CreateReceiptPhysicalItem>;
}

export interface CreateReceiptItem extends ReceiptItem {
  createPhysicalItems: Record<string, CreateReceiptPhysicalItem>;
  defaultOption?: DefaultItemAutocompleteOption;
  itemType?: 'sku' | 'alias';
  aliasID?: number | string;
  customerItemID?: number | string;
}

export interface CreateReceiptPackage extends ReceiptPackage {
  category: UnitOfMeasureCategoryType | '';
  createItems: Record<string, CreateReceiptItem>;
}

type FormStateTypes = 'create' | 'edit';

export interface ReceiptState {
  /**
   * Used to tell typescript what type of form is being used
   */
  stateType: FormStateTypes;
  customerID: number | string;
  warehouseID: number | string;
  referenceNumber: string;
  purchaseOrderNumber: string;
  createNote: string;
  isTplArrangeShipment: boolean;
  savedOnHold: boolean;
  type: 'Receipt' | 'Return';
  files: FileType[];
  receiptShipment?: ReceiptShipment;
  createPackages: Record<string, CreateReceiptPackage>;
  simpleItems: Record<string, SimpleReceiptItem>;
  packagingMode: PackagingMode;
  returnOrderShipments: Record<string, number>;
  returnInspectionPreferenceOverride?: ReceiptReturnInspectionType | '';
  returnFulfillmentType?: AppointmentFulfillmentType | null;
  checkInSmallParcelRequest: CheckInSmallParcelReceiptRequest;
  carrierEntryMode?: 'existing' | 'manual';
}

export interface EditReceiptPackagesState {
  /**
   * Used to tell typescript what type of form is being used
   */
  stateType: FormStateTypes;
  createPackages: Record<string, CreateReceiptPackage>;
}

export interface PageProps {
  receiptId?: string | number;
  receiptType?: ReceiptType;
  customerId?: string | number;
  receiptStatus?: ReceiptStatusType;
  warehouseId?: number;
  warehouseTimezone?: string;
  shipmentId?: number;
  fulfillmentType?: ReceiptFulfillmentType | null;
  carrierArrangedBy?: ReceiptCarrierArrangedByType | null;
}

export interface UpsertTransportation extends PageProps {
  handleClose: () => void;
  isReturnShipment?: boolean;
}

export const defaultReceiptPackage: CreateReceiptPackage = {
  category: '',
  createItems: {},
  packageType: '',
  packageQuantityInGroup: '',
  items: [],
  createItemAliases: [],
};

export const defaultReceiptItem: CreateReceiptItem = {
  createPhysicalItems: {},
  quantityInPackage: 1,
  itemUnitOfMeasureID: '',
  itemQuantityInGroup: '',
  physicalItems: [],
  itemType: 'sku',
};

export const defaultSimpleReceiptItem: SimpleReceiptItem = {
  itemUnitOfMeasureID: '',
  quantity: 1,
  itemType: 'sku',
  createPhysicalItems: {},
};

export const defaultReceiptCheckInSmallParcel: CheckInSmallParcelReceiptRequest =
  {
    palletsCount: '',
    containerNumber: '',
    managerUserID: '',
    warehouseStagingLocationName: '',
    saveInNonSpecificLocation: false,
  };

export function generateReceiptPayload(
  state: ReceiptState,
  documents: ReceiptDocument[] = [],
): ReceiptAPI {
  const {
    isTplArrangeShipment,
    receiptShipment: _receiptShipment,
    createPackages,
    simpleItems,
    packagingMode,
    returnOrderShipments,
    files,
    carrierEntryMode,
    ...rest
  } = state;

  const returnOrderShipmentIDs = Object.values(returnOrderShipments).filter(
    (s) => s > 0,
  );
  const hasLinkedOrders =
    rest.type === 'Return' && returnOrderShipmentIDs.length > 0;

  let receiptShipment: ReceiptShipment | undefined;

  if (!hasLinkedOrders) {
    receiptShipment = _receiptShipment ?? ({} as ReceiptShipment);

    receiptShipment.carrierArrangedBy = isTplArrangeShipment
      ? 'Tpl'
      : 'TplCustomer';

    if (
      carrierEntryMode === 'manual' &&
      receiptShipment.manualShipment &&
      !receiptShipment.manualShipment.carrierServiceCode
    ) {
      receiptShipment.manualShipment = {
        ...receiptShipment.manualShipment,
        carrierServiceCode: 'Unspecified',
      };
    }
  }

  const { packages, nonPackedItems } =
    packagingMode === 'simple'
      ? generateSimpleItemsPayload(simpleItems)
      : {
          packages: generateReceiptPackages(createPackages),
          nonPackedItems: generateNonPackageItems(createPackages),
        };

  return {
    ...rest,
    documents,
    receiptShipment,
    returnOrderShipmentIDs,
    packages,
    nonPackedItems,
  };
}

export function generateSimpleItemsPayload(
  simpleItems: Record<string, SimpleReceiptItem>,
): { packages: Array<ReceiptPackage>; nonPackedItems: Array<ReceiptItem> } {
  const looseSkuItems: SimpleReceiptItem[] = [];
  const aliasItems: SimpleReceiptItem[] = [];
  const masterItems: SimpleReceiptItem[] = [];
  const shipAsReceivedItems: SimpleReceiptItem[] = [];

  // Deduplicate: combine rows with the same UOM or alias by summing quantities
  const deduped: Record<string, SimpleReceiptItem> = {};
  Object.values(simpleItems).forEach((item) => {
    if (item.itemType === 'alias') {
      if (!item.aliasID) return;
      const key = `alias-${item.aliasID}`;
      if (deduped[key]) {
        deduped[key] = {
          ...deduped[key],
          quantity: Number(deduped[key].quantity) + Number(item.quantity),
          createPhysicalItems: {
            ...deduped[key].createPhysicalItems,
            ...item.createPhysicalItems,
          },
        };
      } else {
        deduped[key] = { ...item };
      }
    } else {
      if (!item.itemUnitOfMeasureID) return;
      const key = `sku-${item.itemUnitOfMeasureID}`;
      if (deduped[key]) {
        deduped[key] = {
          ...deduped[key],
          quantity: Number(deduped[key].quantity) + Number(item.quantity),
          createPhysicalItems: {
            ...deduped[key].createPhysicalItems,
            ...item.createPhysicalItems,
          },
        };
      } else {
        deduped[key] = { ...item };
      }
    }
  });

  Object.values(deduped).forEach((item) => {
    if (item.itemType === 'alias') {
      aliasItems.push(item);
      return;
    }
    if (item.unitOfMeasureType === 'Master') {
      masterItems.push(item);
    } else if (
      item.unitOfMeasureType === 'Secondary' ||
      item.isPrimaryUnitPallet
    ) {
      shipAsReceivedItems.push(item);
    } else {
      looseSkuItems.push(item);
    }
  });

  const packages: Array<ReceiptPackage> = [];
  const nonPackedItems: Array<ReceiptItem> = [];

  if (looseSkuItems.length > 0 || aliasItems.length > 0) {
    packages.push({
      packageType: 'Mixed',
      packageQuantityInGroup: 1,
      items: looseSkuItems.map((item) => ({
        itemUnitOfMeasureID: item.itemUnitOfMeasureID,
        quantityInPackage: Number(item.quantity) || 1,
        physicalItems: generateReceiptPhysicalItems(
          item.createPhysicalItems || {},
        ),
      })),
      createItemAliases: aliasItems.map((item) => ({
        aliasID: Number(item.aliasID),
        quantity: Number(item.quantity) || 0,
      })) as CreateReceiptItemAliasAPI[],
    });
  }

  masterItems.forEach((item) => {
    nonPackedItems.push({
      itemUnitOfMeasureID: item.itemUnitOfMeasureID,
      itemQuantityInGroup: Number(item.quantity) || 1,
      quantityInPackage: 1,
      physicalItems: generateReceiptPhysicalItems(
        item.createPhysicalItems || {},
      ),
    });
  });

  shipAsReceivedItems.forEach((item) => {
    nonPackedItems.push({
      itemUnitOfMeasureID: item.itemUnitOfMeasureID,
      itemQuantityInGroup: Number(item.quantity) || 1,
      quantityInPackage: 1,
      physicalItems: generateReceiptPhysicalItems(
        item.createPhysicalItems || {},
      ),
    });
  });

  return { packages, nonPackedItems };
}

export function generateReceiptPackages(
  createPackages: Record<string, CreateReceiptPackage>,
): Array<ReceiptPackage> {
  return Object.keys(createPackages)
    .filter((d) => createPackages[d].category === 'NonMaster')
    .map((d) => {
      const { category, createItems, ...rest } = createPackages[d];

      const items = generateReceiptItems(
        Object.keys(createItems).reduce(
          (acc, key) => {
            if (createItems[key].itemType !== 'alias') {
              acc[key] = createItems[key];
            }
            return acc;
          },
          {} as Record<string, CreateReceiptItem>,
        ),
      );

      const createItemAliases = Object.keys(createItems)
        .filter((key) => createItems[key].itemType === 'alias')
        .map((key) => ({
          aliasID: Number(createItems[key].aliasID),
          quantity: Number(createItems[key].quantityInPackage) || 0,
        })) as CreateReceiptItemAliasAPI[];

      return {
        ...rest,
        items,
        createItemAliases,
      };
    });
}

export function generateNonPackageItems(
  createPackages: Record<string, CreateReceiptPackage>,
): Array<ReceiptItem> {
  return Object.keys(createPackages)
    .filter((d) => createPackages[d].category !== 'NonMaster')
    .reduce<Array<ReceiptItem>>((acc, cur) => {
      const { createItems, packageQuantityInGroup } = createPackages[cur];
      acc.push(
        ...generateReceiptItems(
          createItems,
          packageQuantityInGroup || undefined,
        ),
      );
      return acc;
    }, []);
}

function generateReceiptItems(
  createItems: Record<string, CreateReceiptItem>,
  packageQuantityInGroup?: number,
): Array<ReceiptItem> {
  return Object.keys(createItems).map((i) => {
    const { createPhysicalItems, ...rest } = createItems[i];
    return {
      ...rest,
      itemQuantityInGroup: packageQuantityInGroup,
      physicalItems: generateReceiptPhysicalItems(createPhysicalItems),
    };
  });
}

export function generateReceiptPhysicalItems(
  physicalItems: Record<string, ReceiptPhysicalItem>,
): Array<ReceiptPhysicalItem> {
  return Object.keys(physicalItems).map((i) => {
    return physicalItems[i];
  });
}

export const packageTypeCategoryMapping: Record<
  ReceiptPackageType,
  UnitOfMeasureCategoryType
> = {
  Master: 'Master',
  ShipAsReceived: 'PrePacked',
  Mixed: 'NonMaster',
  Partial: 'NonMaster',
};

export function generateEditData(data: ReceiptAPI): ReceiptState {
  const {
    documents,
    nonPackedItems,
    receiptShipment,
    packages,
    createdBy,
    customer,
    guid,
    importMethod,
    labelDownloadLink,
    ...rest
  } = data;

  const rv = { ...rest } as ReceiptState;
  rv.packagingMode = 'packages';
  rv.simpleItems = {};
  rv.createPackages = {
    ...getReceiptPackageEditData(packages),
    ...getReceiptNonPackedItemsEditData(nonPackedItems),
  };

  return rv;
}

export function getReceiptPackageEditData(
  packages: Array<ReceiptPackage>,
): Record<string, CreateReceiptPackage> {
  return packages.reduce<Record<string, CreateReceiptPackage>>(
    (acc, cur) => {
      const { items, ...rest } = cur;
      acc[cur.id + ''] = {
        ...rest,
        items: [],
        category: packageTypeCategoryMapping[cur.packageType],
        createItems: getReceiptItemsEditData(items),
      };
      return acc;
    },
    {} as Record<string, CreateReceiptPackage>,
  );
}

export function getReceiptNonPackedItemsEditData(
  items: Array<ReceiptItem>,
): Record<string, CreateReceiptPackage> {
  return items.reduce<Record<string, CreateReceiptPackage>>(
    (acc, cur) => {
      const packageType: ReceiptPackageType =
        cur.unitOfMeasure === 'Master' ? 'Master' : 'ShipAsReceived';
      acc[makeuuid(7)] = {
        category: packageTypeCategoryMapping[packageType],
        createItems: getReceiptItemsEditData([cur]),
        packageType,
        packageQuantityInGroup: cur.itemQuantityInGroup || 1,
        items: [],
        createItemAliases: [],
      };
      return acc;
    },
    {} as Record<string, CreateReceiptPackage>,
  );
}

function getReceiptItemsEditData(
  items: Array<ReceiptItem>,
): Record<string, CreateReceiptItem> {
  return items.reduce<Record<string, CreateReceiptItem>>(
    (acc, cur) => {
      const { physicalItems, ...rest } = cur;
      acc[cur.id + ''] = {
        ...rest,
        createPhysicalItems: getReceiptPhysicalItemsEditData(physicalItems),
        physicalItems: [],
        itemType: 'sku',
      };
      return acc;
    },
    {} as Record<string, CreateReceiptItem>,
  );
}

function getReceiptPhysicalItemsEditData(
  physicalItems: Array<ReceiptPhysicalItem>,
): Record<string, CreateReceiptPhysicalItem> {
  return physicalItems.reduce<Record<string, CreateReceiptPhysicalItem>>(
    (acc, cur) => {
      acc[cur.id + ''] = convertNullFieldsToEmptyString(cur);
      return acc;
    },
    {} as Record<string, CreateReceiptPhysicalItem>,
  );
}

const packageSchema = object().shape({
  packageType: string().required('required'),
  packageQuantityInGroup: string().required('required'),
});

const returnShipmentSchema = object().shape({
  fulfillmentType: string().required('required'),
  manualShipment: object().shape({
    carrierCode: string().required('required'),
    carrierServiceCode: string(),
    trackingNumber: string(),
  }),
});

export const receiptSchema = object().shape({
  customerID: string().required('required'),
  warehouseID: string().required('required'),
  createPackages: array()
    .of(packageSchema)
    .nullable()
    .when('type', {
      is: 'Return',
      then: (schema) => schema.optional(),
      otherwise: (schema) =>
        schema.of(packageSchema).min(1, 'At least one package is required'),
    }),
  receiptShipment: object().when(['type', 'returnOrderShipments'], {
    is: (type, returnOrderShipments) => {
      const hasSelectedShipments = Object.values(
        returnOrderShipments || {},
      ).some((value: any) => value > 0);
      return type === 'Return' && !hasSelectedShipments;
    },
    then: returnShipmentSchema,
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const editReceiptPackageSchema = object().shape({
  createPackages: createNestedSchema(packageSchema),
});

export const transportationSchema = object().shape({
  carrierArrangedBy: string().required('required'),
  fulfillmentType: string().required('required'),
  carrierCode: string()
    .nullable()
    .when('carrierArrangedBy', {
      is: (carrierArrangedBy: ReceiptCarrierArrangedByType) =>
        carrierArrangedBy === 'TplCustomer',
      then: string().nullable().required('Required'),
    }),
  carrierServiceCode: string()
    .nullable()
    .when(['carrierArrangedBy', 'carrierEntryMode', 'fulfillmentType'], {
      is: (
        carrierArrangedBy: ReceiptCarrierArrangedByType,
        carrierEntryMode?: string,
        fulfillmentType?: string,
      ) =>
        carrierArrangedBy === 'TplCustomer' &&
        carrierEntryMode === 'existing' &&
        fulfillmentType !== 'Freight',
      then: string().nullable().required('Required'),
    }),
});

export const receiptIssueStatusColors = {
  Open: '#FF4F5B',
  Resolved: '#FFBD00',
};

export interface AssigneeActivityDataProps {
  nickname?: string;
  totalDurationSeconds: number;
  isCompleted: boolean;
  totalExpense: number;
  inventoryTransferID?: number;
  distinctUserIDs: Array<number>;
  activityUsersPerTask: Array<UserPerTaskType>;
  assigneesAlreadyTracking: number[];
  totalDurationDisplay: string;
  assigneeActivities: Array<AssigneeActivityAPI>;
  assigneeIds: string[];
  assigneeRows: Record<string, AssigneeActivityAPI>;
}
